<script lang="ts" setup>
import RichTextEditor from "@/components/text-editor/index.vue";
import { useAppStore } from "@/store/app";
import {
  dateRangeShortcuts,
  dateTypeFieldFormatDayjsForCondition,
  getDateTypeFieldValue,
  getOptionIconVNode,
  includeEndText,
  includeStartText,
  dateTypeFieldFormat as resolveDateTypeFieldFormat,
  dateTypeFieldFormatDayjs as resolveDateTypeFieldFormatDayjs,
  shouldHideInput,
} from "@/util/conditional-logic-helpers";
import { IS_EMPTY, IS_FILLED } from "@/util/constants";
import { allOperationOptions, standardFields } from "@/util/methods";
import { Trash02Icon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UIAlert,
  UICheckbox,
  UIDatepicker,
  UIInput,
  UIModalFooter,
  UISelect,
  UITabPane,
  UITabs,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { cloneDeep } from "lodash";
import { VNode, computed, h, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

// Example URL constants (not translated - these are literal URL examples)
const URL_EXAMPLES = {
  simple: "example.com",
  https: "https://example.com",
  withPath: "example.com/path",
  fullPath: "https://www.example.com/path",
};

dayjs.extend(customParseFormat);
type FieldMode = "field" | "slide";
interface ConditionRow {
  selectedField: string | null;
  selectedOperation: string | null;
  inputValue: any;
  rangeStart?: any;
  rangeEnd?: any;
  includeStart: boolean;
  includeEnd: boolean;
  fieldMode?: FieldMode;
  dateFormat?: string;
}
const props = defineProps({
  editIndex: {
    type: Number,
    default: 0,
  },
  type: {
    type: String,
    default: "displayCustomMessage",
  },
});

const showPhoneAlert = ref(false);
const disqualifyActionOptions = [
  { label: t("form.conditonalLogic.showCustomMessage"), value: "showCustomMessage" },
  { label: t("common.openUrl"), value: "openUrl" },
];

const disqualifyTimingOptions = [
  { label: t("common.disqualifyImmediately"), value: "disqualifyImmediately" },
  { label: t("common.disqualifyAfterSubmit"), value: "disqualifyAfterSubmit" },
];
const showHideOptions = [
  { label: t("form.conditonalLogic.hideField"), value: "Hide" },
  { label: t("form.conditonalLogic.showField"), value: "Show" },
  { label: t("form.conditonalLogic.hideFields"), value: "Hide Multiple" },
  { label: t("form.conditonalLogic.showFields"), value: "Show Multiple" },
];
const showMultiple = computed(() => hideType.value.includes("Multiple"));

const store = useAppStore();
const emit = defineEmits(["onSave"]);
const sharedSelectedOperation = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].conditionalOperation || "then"
);
const disqualifyTiming = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.disqualifyTiming ||
    "disqualifyAfterSubmit"
);
const disqualifyAction = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.disqualifyAction ||
    "showCustomMessage"
);
const outcomeValue = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.value || ""
);
const hideType = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.hideType || "Hide"
);
const jumpTarget = ref(
  store.builderConfig.conditionalLogic?.[props.editIndex].outcome?.jumpTarget || ""
);
const toConditionRow = (c: any): ConditionRow => {
  const isBetween = c?.selectedOperation === "between";
  let inputPayload = c?.inputValue;
  if (isBetween && typeof inputPayload === "string") {
    try {
      inputPayload = JSON.parse(inputPayload);
    } catch {
      inputPayload = undefined;
    }
  }

  const hydratedRangeStart =
    c?.rangeStart !== undefined && c?.rangeStart !== null && c?.rangeStart !== ""
      ? c.rangeStart
      : isBetween
      ? inputPayload?.start
      : undefined;
  const hydratedRangeEnd =
    c?.rangeEnd !== undefined && c?.rangeEnd !== null && c?.rangeEnd !== ""
      ? c.rangeEnd
      : isBetween
      ? inputPayload?.end
      : undefined;
  const hydratedIncludeStart =
    typeof c?.includeStart === "boolean"
      ? c.includeStart
      : isBetween && typeof inputPayload?.includeStart === "boolean"
      ? inputPayload.includeStart
      : true;
  const hydratedIncludeEnd =
    typeof c?.includeEnd === "boolean"
      ? c.includeEnd
      : isBetween && typeof inputPayload?.includeEnd === "boolean"
      ? inputPayload.includeEnd
      : true;

  // Derive fieldMode for legacy Jump To rules without fieldMode
  let hydratedFieldMode: FieldMode | undefined = c?.fieldMode as FieldMode | undefined;
  if (!hydratedFieldMode && store.surveyId && props.type === "jumpTo") {
    const slideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
    const selected = String(c?.selectedField || "");
    hydratedFieldMode = selected && slideIds.has(selected) ? "slide" : "field";
  }

  return {
    selectedField: c?.selectedField ?? null,
    selectedOperation: c?.selectedOperation ?? null,
    inputValue: c?.inputValue ?? null,
    rangeStart: hydratedRangeStart,
    rangeEnd: hydratedRangeEnd,
    includeStart: hydratedIncludeStart,
    includeEnd: hydratedIncludeEnd,
    fieldMode: hydratedFieldMode ?? (c?.fieldMode as FieldMode | undefined) ?? undefined,
    dateFormat: c?.dateFormat ?? undefined,
  };
};

const initialRawConditions =
  (store.builderConfig.conditionalLogic?.[props.editIndex]?.conditions as any[]) || [];

const conditions = ref<ConditionRow[]>(
  initialRawConditions.length
    ? initialRawConditions.map((c) => toConditionRow(c))
    : [
        {
          selectedField: null,
          selectedOperation:
            props.type === "jumpTo" || props.type === "disqualifyLead" ? "isEqualTo" : null,
          inputValue: null,
          rangeStart: undefined as any,
          rangeEnd: undefined as any,
          includeStart: true,
          includeEnd: true,
          fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
        },
      ]
);

// Container for teleporting embed modals (image/link) inside the conditional logic modal
const clEditorContainer = ref<HTMLElement | null>(null);

const addCondition = (condition, index) => {
  sharedSelectedOperation.value = condition;
  if (condition !== "then" && index === conditions.value.length - 1) {
    conditions.value.push({
      selectedField: null,
      selectedOperation:
        props.type === "jumpTo" || props.type === "disqualifyLead" ? "isEqualTo" : null,
      inputValue: null,
      rangeStart: undefined,
      rangeEnd: undefined,
      includeStart: true,
      includeEnd: true,
      fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
    });
  }
};

const deleteCondition = (index) => {
  conditions.value.splice(index, 1);
  if (conditions.value.length === 0) {
    conditions.value.push({
      selectedField: null,
      selectedOperation: null,
      inputValue: null,
      rangeStart: undefined,
      rangeEnd: undefined,
      includeStart: true,
      includeEnd: true,
      fieldMode: store.surveyId && props.type === "jumpTo" ? "field" : undefined,
    });
  }
};

const isConditionInvalid = (condition) => {
  // Jump To (Slide basis): only slide selection is required; no value input
  if (props.type === "jumpTo" && store.surveyId && condition.fieldMode === "slide") {
    return !condition.selectedField;
  }
  if ([IS_EMPTY, IS_FILLED, "isChecked", "isNotChecked"].includes(condition.selectedOperation)) {
    return false;
  }
  if (condition.selectedOperation === "between") {
    const hasStart =
      condition.rangeStart !== undefined &&
      condition.rangeStart !== null &&
      condition.rangeStart !== "";
    const hasEnd =
      condition.rangeEnd !== undefined && condition.rangeEnd !== null && condition.rangeEnd !== "";
    return !(condition.selectedField && condition.selectedOperation && hasStart && hasEnd);
  }
  return !(
    condition.selectedField &&
    condition.selectedOperation &&
    (condition.inputValue || condition.inputValue === 0)
  );
};

const isSaveDisabled = computed(
  () =>
    conditions.value.some((condition) => {
      // Jump To (Slide basis): valid when slide is chosen
      if (props.type === "jumpTo" && store.surveyId && condition.fieldMode === "slide") {
        return !condition.selectedField;
      }
      if (
        [IS_EMPTY, IS_FILLED, "isChecked", "isNotChecked"].includes(condition.selectedOperation)
      ) {
        return false;
      }
      if (condition.selectedOperation === "between") {
        const hasStart =
          condition.rangeStart !== undefined &&
          condition.rangeStart !== null &&
          condition.rangeStart !== "";
        const hasEnd =
          condition.rangeEnd !== undefined &&
          condition.rangeEnd !== null &&
          condition.rangeEnd !== "";
        return !(condition.selectedField && condition.selectedOperation && hasStart && hasEnd);
      }
      return !(
        condition.selectedField &&
        condition.selectedOperation &&
        (condition.inputValue || condition.inputValue === 0)
      );
    }) ||
    (props.type !== "jumpTo" &&
      !Array.isArray(outcomeValue.value) &&
      outcomeValue.value.trim() === "") ||
    (props.type === "jumpTo" && !jumpTarget.value) ||
    !!cycleErrorMessage.value
);

// --- Cycle protection (Jump To rules) ---
const getSlideLabel = (id: string): string => {
  const index = store.slides.findIndex((s: any) => String(s.id) === String(id));
  if (index === -1) {
    return String(id);
  }
  return `P${index + 1} - ${store.slides[index].slideName}`;
};

const getSlideIdsFromRule = (rule: any): string[] => {
  if (!rule || !Array.isArray(rule?.conditions)) {
    return [];
  }
  const allSlideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
  return rule.conditions
    .filter((cond: any) => {
      const sid = String(cond?.selectedField || "");
      return !!sid && (cond?.fieldMode === "slide" || allSlideIds.has(sid));
    })
    .map((cond: any) => String(cond.selectedField));
};

const computeIfSlideIds = (): string[] => {
  const allSlideIds = new Set((store.slides || []).map((s: any) => String(s.id)));
  return (conditions.value || [])
    .filter((c: any) => {
      const sid = String(c?.selectedField || "");
      return (c?.fieldMode === "slide" && !!sid) || (!c?.fieldMode && allSlideIds.has(sid));
    })
    .map((c: any) => String(c.selectedField));
};

const willIntroduceTwoWayCycle = (
  sources: string[],
  target: string
): { a: string; b: string } | null => {
  if (!store.surveyId || !target || !sources?.length) {
    return null;
  }
  const edges = new Map<string, string>();
  const rulesArr: any[] = ((store.builderConfig as any)?.conditionalLogic || []) as any[];
  // existing edges
  rulesArr.forEach((r) => {
    if (r?.outcome?.type !== "jumpTo") {
      return;
    }
    const rTarget = String(r?.outcome?.jumpTarget || "");
    if (!rTarget) {
      return;
    }
    const rSources = getSlideIdsFromRule(r);
    rSources.forEach((s) => edges.set(String(s), rTarget));
  });
  // proposed edges
  sources.forEach((s) => edges.set(String(s), String(target)));
  // detect A<->B
  for (const [a, b] of edges.entries()) {
    if (!b) {
      continue;
    }
    const back = edges.get(String(b));
    if (back === a) {
      return { a, b };
    }
  }
  return null;
};

const cycleErrorMessage = ref<string>("");

watchEffect(() => {
  if (props.type !== "jumpTo" || !store.surveyId) {
    cycleErrorMessage.value = "";
    return;
  }
  const sources = computeIfSlideIds();
  const target = String(jumpTarget.value || "");
  const pair = willIntroduceTwoWayCycle(sources, target);
  if (pair) {
    cycleErrorMessage.value = t("form.conditonalLogic.cycleError", {
      a: getSlideLabel(pair.a),
      b: getSlideLabel(pair.b),
    }) as string;
  } else {
    cycleErrorMessage.value = "";
  }
});

const onSave = () => {
  if (props.type === "jumpTo" && store.surveyId) {
    const sources = computeIfSlideIds();
    const target = String(jumpTarget.value || "");
    const pair = willIntroduceTwoWayCycle(sources, target);
    if (pair) {
      cycleErrorMessage.value = t("form.conditonalLogic.cycleError", {
        a: getSlideLabel(pair.a),
        b: getSlideLabel(pair.b),
      }) as string;
      return; // block save
    }
  }
  conditions.value = conditions.value
    .filter((condition: any) => condition.conditionalOperation !== null)
    .map((condition) => {
      if (condition.selectedOperation === "between") {
        const betweenPayload = {
          start: condition.rangeStart,
          end: condition.rangeEnd,
          includeStart: !!condition.includeStart,
          includeEnd: !!condition.includeEnd,
        };
        return {
          selectedField: condition.selectedField,
          selectedOperation: condition.selectedOperation,
          inputValue: betweenPayload as any,
          ...(condition?.dateFormat ? { dateFormat: condition.dateFormat } : {}),
        } as any;
      }
      if (getType(condition) === "monetory" && !isNaN(parseFloat(condition.inputValue))) {
        return {
          selectedField: condition.selectedField,
          selectedOperation: condition.selectedOperation,
          inputValue: parseFloat(condition.inputValue).toFixed(2),
          ...(condition?.dateFormat ? { dateFormat: condition.dateFormat } : {}),
        } as any;
      }
      return {
        selectedField: condition.selectedField,
        selectedOperation: condition.selectedOperation,
        inputValue: condition.inputValue,
        ...(condition?.dateFormat ? { dateFormat: condition.dateFormat } : {}),
      } as any;
    });

  const savedLogic: any = {
    conditions: conditions.value,
    outcome: { type: props.type, value: outcomeValue.value },
    conditionalOperation: sharedSelectedOperation.value,
  };
  if (props.type === "disqualifyLead") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      disqualifyAction: disqualifyAction.value,
    };

    if (store.surveyId) {
      savedLogic.outcome = {
        ...savedLogic.outcome,
        disqualifyTiming: disqualifyTiming.value,
      };
    }
  }
  if (props.type === "showHideFields") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      hideType: hideType.value,
    };
  }
  if (props.type === "jumpTo") {
    savedLogic.outcome = {
      ...savedLogic.outcome,
      jumpTarget: jumpTarget.value,
      jumpTargetType: store.surveyId ? "slide" : jumpTargetType.value,
    };
    // Two-way sync to sidebar jumpTo when basis is Slide
    if (store.surveyId) {
      const allSlideIds = store.slides.map((s) => String(s.id));
      const slideIds = (conditions.value || [])
        .filter((c: any) => {
          const sid = String(c?.selectedField || "");
          return (
            (c?.fieldMode === "slide" && !!sid) || (!c?.fieldMode && allSlideIds.includes(sid))
          );
        })
        .map((c: any) => String(c.selectedField));
      slideIds.forEach((sid: string) => {
        const slideIndex = store.slides.findIndex((s) => s.id === sid);
        if (slideIndex >= 0) {
          (store.slides[slideIndex] as any).jumpTo = jumpTarget.value;
        }
      });

      // De-duplicate Jump To rules per slide: update existing rule if one already exists
      const rulesArr: any[] = (store.builderConfig as any)?.conditionalLogic || [];
      slideIds.forEach((sid: string) => {
        const matchingIndexes = rulesArr
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => {
            if (r?.outcome?.type !== "jumpTo" || !Array.isArray(r?.conditions)) {
              return false;
            }
            return r.conditions.some(
              (c: any) =>
                (c?.fieldMode === "slide" && c?.selectedField === sid) ||
                (!c?.fieldMode && String(c?.selectedField || "") === sid)
            );
          })
          .map(({ i }) => i);

        if (matchingIndexes.length) {
          const keepIndex = Math.min(...matchingIndexes);
          // Update the kept rule's outcome to latest target
          rulesArr[keepIndex].outcome = {
            ...(rulesArr[keepIndex].outcome || {}),
            jumpTarget: jumpTarget.value,
            jumpTargetType: "slide",
            type: "jumpTo",
          };
          // Remove other duplicates except the kept one
          const toRemove = matchingIndexes.filter((idx) => idx !== keepIndex);
          toRemove
            .sort((a, b) => b - a)
            .forEach((idx) => {
              if (idx >= 0 && idx < rulesArr.length) {
                rulesArr.splice(idx, 1);
              }
            });
        }
      });
      // Persist potential dedup changes
      (store.builderConfig as any).conditionalLogic = rulesArr;

      // Upsert: write savedLogic into existing Jump To rule for the same slide if present
      let writeIndex = props.editIndex as number;
      const firstSid = slideIds[0];
      if (firstSid) {
        const existingIdx = ((store.builderConfig as any).conditionalLogic || []).findIndex(
          (r: any) =>
            r?.outcome?.type === "jumpTo" &&
            Array.isArray(r?.conditions) &&
            r.conditions.some(
              (c: any) =>
                (c?.fieldMode === "slide" && String(c?.selectedField) === firstSid) ||
                (!c?.fieldMode && String(c?.selectedField || "") === firstSid)
            )
        );
        if (existingIdx >= 0) {
          writeIndex = existingIdx;
        }
      }
      (store.builderConfig as any).conditionalLogic[writeIndex] = savedLogic;
      store.anyUnsavedChanges = true;
      console.log("savedLogic", store.builderConfig.conditionalLogic);
      emit("onSave");
      store.addToHistory();
      return;
    }
  }
  store.builderConfig.conditionalLogic[props.editIndex] = savedLogic;
  store.anyUnsavedChanges = true;
  console.log("savedLogic", store.builderConfig.conditionalLogic);
  emit("onSave");
  store.addToHistory();
};

let clonedFields = [];

if (store.formId) {
  clonedFields = cloneDeep(store.elements);
} else if (store.surveyId) {
  clonedFields = store.slides.reduce((acc, slide) => {
    if (slide.slideData && Array.isArray(slide.slideData)) {
      return [...acc, ...slide.slideData];
    }
    return acc;
  }, []);
}

// Handle address settings for both forms and surveys
const groupAddressIndex = clonedFields.findIndex((item) => item.tag === "group_address");
if (groupAddressIndex !== -1) {
  clonedFields.splice(groupAddressIndex, 1, ...store.addressSettings.children);
}

const fieldOptions = computed(() => {
  return clonedFields
    .filter(({ type }) => {
      if (props.type === "jumpTo" || (props.type === "disqualifyLead" && store.surveyId)) {
        // expanded coverage for jumpTo & survey disqualify
        const excluded = [
          ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
          "h1",
          "html",
          "img",
          "captcha",
          "source",
        ];
        return !excluded.includes(type);
      }

      const defaultExcludes = [
        ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
        ...(standardFields?.integration?.group || []).map((x) => x.type),
        "textbox_list",
        "h1",
        "html",
        "img",
        "captcha",
        "source",
      ];
      return !defaultExcludes.includes(type);
    })
    .flatMap((item) => {
      const iconRenderer = () => getOptionIconVNode(item as any);
      // Split T&C into TnC1 and TnC2 when both placeholders exist
      if (item.type === "terms_and_conditions" && item.placeholder && item.placeholder2) {
        return [
          {
            value: `${item.hiddenFieldQueryKey}_1`,
            label: "TnC1",
            type: item.type,
            icon: iconRenderer,
            picklistOptions: null,
          },
          {
            value: `${item.hiddenFieldQueryKey}_2`,
            label: "TnC2",
            type: item.type,
            icon: iconRenderer,
            picklistOptions: null,
          },
        ];
      }
      return [
        {
          value: item.hiddenFieldQueryKey,
          label: item.label,
          type: item.type,
          icon: iconRenderer,
          picklistOptions:
            item.picklistOptions || item?.picklistOptionsImage?.map((x) => x.label) || null,
          allowCustomOption: !!(item as any)?.allowCustomOption,
        },
      ];
    });
});

const showHideFieldOptions = computed(() => {
  return clonedFields
    .filter(({ type }) => {
      const excluded = [
        ...(store.formId ? standardFields.submit.group : []).map((x) => x.type),
        // Exclude captcha from Show/Hide targets
        "captcha",
      ];
      return !excluded.includes(type);
    })
    .flatMap((item) => {
      // Use a renderer function to generate a fresh icon VNode per render
      const iconRenderer = () => getOptionIconVNode(item as any);
      const rawLabel = item.label;
      const plainLabel = (item.label || "").replace(/<\/?[^>]+(>|$)/g, "");
      const truncate = (s: string, max = 32) => (s && s.length > max ? s.slice(0, max) + "…" : s);

      // Split T&C into two targets when both placeholders exist
      if (item.type === "terms_and_conditions" && item.placeholder && item.placeholder2) {
        return [
          {
            value: `${item.hiddenFieldQueryKey}_1`,
            label: "TnC1",
            rawLabel: "TnC1",
            type: item.type,
            icon: iconRenderer,
            picklistOptions: null,
          },
          {
            value: `${item.hiddenFieldQueryKey}_2`,
            label: "TnC2",
            rawLabel: "TnC2",
            type: item.type,
            icon: iconRenderer,
            picklistOptions: null,
          },
        ];
      }

      return [
        {
          value: item.hiddenFieldQueryKey,
          label: truncate(plainLabel),
          rawLabel,
          type: item.type,
          icon: iconRenderer,
          picklistOptions:
            item.picklistOptions || item?.picklistOptionsImage?.map((x) => x.label) || null,
        },
      ];
    });
});

// Jump To target type (Question or Slide)
const jumpTargetType = ref(
  (store.builderConfig.conditionalLogic?.[props.editIndex]?.outcome as any)?.jumpTargetType ||
    "question"
);

const questionOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  return clonedFields.map((item) => ({ value: item.hiddenFieldQueryKey, label: item.label }));
});

// IF (basis) slide options: no "Submit Survey"
const ifSlideOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  return store.slides.map((slide, index) => ({
    value: slide.id,
    label: `P${index + 1} - ${slide.slideName}`,
  }));
});

// Jump target slide options: exclude any selected slide(s) in IF; no "Submit Survey"
const jumpSlideOptions = computed(() => {
  if (!store.surveyId) {
    return [];
  }
  const excluded = new Set(
    (conditions.value || [])
      .filter((c: any) => c?.fieldMode === "slide" && c?.selectedField)
      .map((c: any) => String(c.selectedField))
  );
  return store.slides
    .map((slide, index) => ({ value: slide.id, label: `P${index + 1} - ${slide.slideName}` }))
    .filter((opt) => !excluded.has(String(opt.value)));
});

const operationOptions = ref(allOperationOptions);

const getType = (condition) => {
  if (!condition.selectedField) {
    return null;
  }
  return fieldOptions.value.find((x) => x.value === condition.selectedField)?.type;
};

const getPicklistOptions = (condition) => {
  if (!condition.selectedField) {
    return [];
  }
  const selectedOption = fieldOptions.value.find((x) => x.value === condition.selectedField) as
    | { picklistOptions?: string[]; allowCustomOption?: boolean }
    | undefined;
  const baseOptions =
    selectedOption?.picklistOptions?.map((item) => ({ value: item, label: item })) || [];
  const selectedType = getType(condition);
  if (selectedType === "radio" && !!selectedOption?.allowCustomOption) {
    return [...baseOptions, { value: "Other", label: t("common.other") }];
  }
  return baseOptions;
};

const updateOptions = (condition, value, oldSelectedField = null) => {
  if (!value) {
    return;
  }

  const oldSelectedType = oldSelectedField
    ? fieldOptions.value.find((x) => x.value === oldSelectedField)?.type
    : null;
  const selectedFieldOption = fieldOptions.value.find((x) => x.value === value);
  const selectedType = selectedFieldOption?.type;

  if (oldSelectedField && oldSelectedType !== selectedType) {
    condition.inputValue = "";
    if (
      selectedType &&
      ["single_options", "radio", "checkbox", "multiple_options"].includes(selectedType)
    ) {
      condition.inputValue = getPicklistOptions(condition)?.[0]?.value || "";
    }
    if (selectedType === "date") {
      condition.inputValue = dayjs().format("YYYY-MM-DD");
    }
    condition.rangeStart = undefined;
    condition.rangeEnd = undefined;
    condition.includeStart = true;
    condition.includeEnd = true;
    condition.dateFormat = undefined;
  }

  showPhoneAlert.value = selectedType === "phone" || value === "phone";

  // Use the same operation filtering for all types (including disqualifyLead)
  operationOptions.value = allOperationOptions.filter((option) =>
    selectedType ? option.types.includes(selectedType) : false
  );

  // Ensure selected operation is valid for the current options
  if (!operationOptions.value.some((o) => o.value === condition.selectedOperation)) {
    condition.selectedOperation = operationOptions.value[0]?.value || null;
  }
};

const hideInput = (condition) => shouldHideInput(condition.selectedOperation);

const resolveOptionIcon = (option: any): VNode | null => {
  try {
    const maybe = typeof option.icon === "function" ? option.icon() : option.icon;
    return maybe || null;
  } catch {
    return null;
  }
};

const renderFieldOption = ({ node, option }: { node: VNode; option: any }) =>
  h("div", { class: "relative flex items-center w-full" }, [
    h(
      "div",
      {
        class: [
          "option-icon ml-2 shrink-0 flex items-center justify-center",
          option.type === "rating" ? "w-8 h-8 ml-[4px] mr-[-4px]" : "w-6 h-6",
        ].join(" "),
      },
      [resolveOptionIcon(option)]
    ),
    h("div", { class: "w-full" }, [node]),
  ]);

const renderOptions = ({ node, option }: { node: VNode; option }) =>
  h(
    UITooltip,
    { placement: "left-start" },
    {
      trigger: () => node,
      default: () => option.label,
    }
  );
/** Use condition's stored format when setting datepicker value (parsing). */
const dateFormatForParsing = (condition: any) =>
  dateTypeFieldFormatDayjsForCondition(store, condition);

const dateTypeFieldValue = (condition: any) =>
  getDateTypeFieldValue(store, condition.selectedField, condition.inputValue, condition.dateFormat);

const dateFormatUI = (condition: any) => resolveDateTypeFieldFormat(store, condition.selectedField);

/** Use store format when writing date to condition; also set condition.dateFormat to this. */
const dateFormatDayjs = (condition: any) =>
  resolveDateTypeFieldFormatDayjs(store, condition.selectedField);

const safeDayjs = (date: string, condition: any) => {
  const value = dayjs(date, dateFormatForParsing(condition));
  return value.isValid() ? value.valueOf() : null;
};
</script>

<template>
  <div class="divider h-[610px] overflow-auto">
    <div class="flex justify-between py-3 font-medium">
      {{ t("form.conditonalLogic.logic.if") }}
      <Trash02Icon
        v-if="conditions.length > 1"
        class="mr-3 h-4 w-4 cursor-pointer text-error-700"
        @click="deleteCondition(0)"
      />
    </div>
    <template v-for="(condition, index) in conditions" :key="index">
      <div class="mb-2 flex flex-col gap-y-2 text-base">
        <div
          :class="
            props.type === 'jumpTo' && store.surveyId
              ? condition.fieldMode === 'slide'
                ? 'mb-8 grid grid-cols-2 items-center gap-x-2'
                : 'mb-8 grid grid-cols-4 items-center gap-x-2'
              : 'mb-8 flex gap-x-2 px-0.5'
          "
        >
          <!-- Jump To (Surveys): Field or Slide basis selector -->
          <UISelect
            v-if="props.type === 'jumpTo' && store.surveyId"
            :id="`fieldMode-${index}`"
            class="w-full"
            :value="condition.fieldMode || 'field'"
            :options="[
              { label: t('form.conditonalLogic.field'), value: 'field' },
              { label: t('form.conditonalLogic.slide'), value: 'slide' },
            ]"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectFieldOrSlide')"
            filterable
            @update:value="
              (v) => {
                condition.fieldMode = v;
                condition.selectedField = null;
                condition.inputValue = null;
                condition.rangeStart = undefined;
                condition.rangeEnd = undefined;
                condition.includeStart = true;
                condition.includeEnd = true;
                condition.dateFormat = undefined;
                condition.selectedOperation = 'isEqualTo';
              }
            "
          />
          <UISelect
            v-if="!(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide')"
            :id="`selectedField-${index}`"
            :class="
              props.type === 'jumpTo' && store.surveyId
                ? 'w-full'
                : condition.selectedOperation === 'between'
                ? 'w-1/4'
                : 'w-full'
            "
            :value="condition.selectedField"
            :options="fieldOptions"
            :render-option="renderFieldOption"
            :placeholder="t('form.conditonalLogic.selectField')"
            filterable
            @update:value="
              (value) => {
                const oldSelectedField = condition.selectedField;
                condition.selectedField = value;
                updateOptions(condition, value, oldSelectedField);
                condition.selectedOperation = operationOptions[0].value;
              }
            "
          />
          <!-- Slide chooser when basis is Slide -->
          <UISelect
            v-else
            :id="`selectedSlide-${index}`"
            class="w-full"
            :value="condition.selectedField"
            :options="ifSlideOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectSlideToJumpTo')"
            filterable
            @update:value="
              (value) => {
                condition.selectedField = value;
                // No value input is required for slide-level IF
              }
            "
          />
          <!-- For jumpTo, operation is implicitly isEqualTo; no selector needed -->
          <UISelect
            v-if="
              props.type !== 'jumpTo' ||
              (props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'field')
            "
            :id="`selectedOperation-${index}`"
            :class="
              props.type === 'jumpTo' && store.surveyId
                ? 'w-full'
                : condition.selectedOperation === 'between'
                ? 'w-1/4'
                : 'w-full'
            "
            :value="condition.selectedOperation"
            :options="operationOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectFieldState')"
            filterable
            :disabled="!condition.selectedField"
            @click="updateOptions(condition, condition.selectedField)"
            @update:value="(value) => (condition.selectedOperation = value)"
          />
          <!-- Value input renders only when basis is Field -->
          <UIDatepicker
            v-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date' &&
              condition.selectedOperation === 'between'
            "
            :id="`input_datepicker_range-${index}`"
            :class="props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-1/2'"
            type="daterange"
            :shortcuts="dateRangeShortcuts()"
            :value="
              (condition.rangeStart && condition.rangeEnd && safeDayjs(condition.rangeStart, condition) && safeDayjs(condition.rangeEnd, condition)
                ? [
                    safeDayjs(condition.rangeStart, condition),
                    safeDayjs(condition.rangeEnd, condition),
                  ]
                : null) as any
            "
            :format="dateFormatUI(condition)"
            @update:value="
              (values) => {
                const [start, end] = values || [];
                const format = dateFormatDayjs(condition);
                condition.dateFormat = format;
                condition.rangeStart = start ? dayjs(start).format(format) : undefined;
                condition.rangeEnd = end ? dayjs(end).format(format) : undefined;
              }
            "
          />
          <UIDatepicker
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              getType(condition) === 'date'
            "
            id="input_datepicker"
            :class="[
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :value="dateTypeFieldValue(condition)"
            :format="dateFormatUI(condition)"
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:value="
              (value) => {
                const format = dateFormatDayjs(condition);
                condition.dateFormat = format;
                condition.inputValue = dayjs(value).format(format);
              }
            "
          />
          <UISelect
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              ['single_options', 'radio', 'checkbox', 'multiple_options'].includes(
                getType(condition)
              ) &&
              condition.selectedOperation !== 'between'
            "
            :id="`picklistOptions-${index}`"
            :class="[
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :value="condition.inputValue"
            :options="getPicklistOptions(condition)"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectOption')"
            filterable
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:value="(value) => (condition.inputValue = value)"
          />
          <div
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide') &&
              condition.selectedOperation === 'between' &&
              ['numerical', 'monetory', 'score', 'rating'].includes(getType(condition))
            "
            :class="
              props.type === 'jumpTo' && store.surveyId ? 'flex w-full gap-2' : 'flex w-1/2 gap-2'
            "
          >
            <UIInput
              :id="`inputValueStart-${index}`"
              class="w-1/2"
              :model-value="condition.rangeStart"
              :placeholder="t('common.startValue')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:model-value="(value) => (condition.rangeStart = value)"
            />
            <UIInput
              :id="`inputValueEnd-${index}`"
              class="w-1/2"
              :model-value="condition.rangeEnd"
              :placeholder="t('common.endValue')"
              :disabled="!condition.selectedField || !condition.selectedOperation"
              @update:model-value="(value) => (condition.rangeEnd = value)"
            />
          </div>
          <UIInput
            v-else-if="
              !(props.type === 'jumpTo' && store.surveyId && condition.fieldMode === 'slide')
            "
            :id="`inputValue-${index}`"
            :class="[
              hideInput(condition) ? 'invisible' : 'visible',
              props.type === 'jumpTo' && store.surveyId ? 'w-full' : 'w-full',
            ]"
            :model-value="condition.inputValue"
            :placeholder="t('form.conditonalLogic.typeValueHere')"
            :disabled="!condition.selectedField || !condition.selectedOperation"
            @update:model-value="(value) => (condition.inputValue = value)"
          />
        </div>
        <div
          v-if="condition.selectedOperation === 'between'"
          class="-mt-6 mb-4 flex items-center gap-6 text-sm text-gray-700"
        >
          <div class="flex items-center gap-2">
            <UICheckbox
              id="include-start"
              :checked="condition.includeStart"
              @update:checked="(v) => (condition.includeStart = !!v)"
            />
            <span>{{
              includeStartText(
                condition,
                getType(condition),
                dateFormatUI(condition)?.toUpperCase()
              )
            }}</span>
          </div>
          <div class="flex items-center gap-2">
            <UICheckbox
              id="include-end"
              :checked="condition.includeEnd"
              @update:checked="(v) => (condition.includeEnd = !!v)"
            />
            <span>{{
              includeEndText(condition, getType(condition), dateFormatUI(condition)?.toUpperCase())
            }}</span>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div :class="index === conditions.length - 1 ? 'w-[88%]' : 'w-3/5'">
            <div v-if="props.type === 'jumpTo'" class="font-medium text-gray-600">
              {{ t("form.conditonalLogic.logic.then") }}
            </div>
            <UITabs
              v-else
              default-value="then"
              type="segment"
              :value="index === conditions.length - 1 ? 'then' : sharedSelectedOperation"
              @update:value="(condition) => addCondition(condition, index)"
            >
              <UITabPane
                v-if="index === conditions.length - 1"
                name="then"
                :tab="t('form.conditonalLogic.logic.then')"
                >{{ t("form.conditonalLogic.logic.then") }}</UITabPane
              >
              <UITabPane
                name="and"
                :tab="t('form.conditonalLogic.logic.and')"
                :disabled="isConditionInvalid(condition)"
              >
                {{ t("form.conditonalLogic.logic.and") }}
              </UITabPane>
              <UITabPane
                name="or"
                :tab="t('form.conditonalLogic.logic.or')"
                :disabled="isConditionInvalid(condition)"
              >
                {{ t("form.conditonalLogic.logic.or") }}</UITabPane
              >
            </UITabs>
          </div>
          <Trash02Icon
            v-if="index !== conditions.length - 1"
            class="mr-3 h-4 w-4 cursor-pointer text-error-700"
            @click="deleteCondition(index + 1)"
          />
        </div>
      </div>
    </template>
    <UIAlert v-if="showPhoneAlert" id="warning-message" type="warning">
      <template #content>{{ t("form.conditonalLogic.phoneAlertMessage") }}</template>
    </UIAlert>
    <div id="output" class="mb-3 mt-3">
      <div class="mb-2 text-xs font-medium text-gray-700">
        <div class="mb-3 flex">
          <div v-if="props.type === 'disqualifyLead'" class="flex w-full gap-2 text-gray-900">
            <UISelect
              v-if="store.surveyId"
              id="disqualify-timing"
              v-model:value="disqualifyTiming"
              class="w-full"
              :options="disqualifyTimingOptions"
              :render-option="renderOptions"
              :placeholder="t('common.select')"
            />
            <UISelect
              :id="`disqualify-action`"
              v-model:value="disqualifyAction"
              class="w-full"
              :options="disqualifyActionOptions"
              :render-option="renderOptions"
              :placeholder="t('common.select')"
              @update:value="() => (outcomeValue = '')"
            />
          </div>
          <div v-if="props.type === 'displayCustomMessage'">
            {{ t("form.conditonalLogic.showCustomMessage") }}
          </div>
        </div>
        <div ref="clEditorContainer">
          <RichTextEditor
            v-if="
              props.type === 'displayCustomMessage' ||
              (props.type === 'disqualifyLead' && disqualifyAction === 'showCustomMessage')
            "
            v-model:modelValue="outcomeValue"
            size="mid"
            :is-submit-message="true"
            :modal-container="clEditorContainer"
          />
        </div>
      </div>
      <div
        v-if="
          props.type === 'redirectToUrl' ||
          (props.type === 'disqualifyLead' && disqualifyAction === 'openUrl')
        "
        class="mb-2 text-xs font-medium text-gray-700"
      >
        {{ t("form.conditonalLogic.openBelowUrl") }}
        <UIInput
          id="redirectToUrlInput"
          v-model="outcomeValue"
          class="my-3 w-full font-normal"
          :placeholder="t('common.exampleUrlPlaceholder')"
        />
        <div class="text-xs font-normal text-gray-600">
          {{ t("common.addUrlLike") }}<span class="underline">{{ URL_EXAMPLES.simple }}</span
          >, <span class="underline">{{ URL_EXAMPLES.https }}</span
          >, <span class="underline">{{ URL_EXAMPLES.withPath }}</span> {{ t("common.or") }}
          <span class="underline">{{ URL_EXAMPLES.fullPath }}</span
          >.
        </div>
      </div>
      <div v-if="props.type === 'showHideFields'" class="mb-2 text-xs font-medium text-gray-700">
        <div class="mb-8 flex gap-x-2">
          <UISelect
            id="showHideFieldType"
            v-model:value="hideType"
            class="w-1/3"
            :options="showHideOptions"
            :render-option="renderOptions"
            :placeholder="t('common.select')"
            filterable
          />
          <UISelect
            id="selectedFieldShowHide"
            v-model:value="outcomeValue"
            :options="showHideFieldOptions"
            :render-option="renderFieldOption"
            :placeholder="t('common.select')"
            filterable
            :multiple="showMultiple"
            type="default"
          />
        </div>
      </div>
      <div v-if="props.type === 'jumpTo'" class="mb-2 text-xs font-medium text-gray-700">
        {{ t("form.conditonalLogic.jumpToDescription") }}
        <div class="mb-8 mt-2 flex gap-x-2">
          <UISelect
            id="jumpToSlide"
            v-model:value="jumpTarget"
            class="w-full"
            :options="jumpSlideOptions"
            :render-option="renderOptions"
            :placeholder="t('form.conditonalLogic.selectSlideToJumpTo')"
            filterable
          />
        </div>

        <UIAlert v-if="cycleErrorMessage" id="cycle-error" type="error">
          <template #content>{{ cycleErrorMessage }}</template>
        </UIAlert>
      </div>
    </div>
  </div>
  <UIModalFooter
    id="modal-footer"
    :positive-text="t('common.save')"
    :disabled="isSaveDisabled"
    :loading="false"
    @positive-click="onSave"
  />
</template>

<style scoped>
.divider {
  border-bottom: 1px solid #eaecf0;
  width: 100%;
}
:deep(.icon-custom) {
  border: 0;
  box-shadow: none;
  display: block;
}
.n-tabs {
  width: 25%;
}
:deep(.n-tabs-tab) {
  border-radius: 6px !important;
}
.n-tab-pane {
  display: none;
}
:deep(.n-tabs-rail) {
  border-radius: 6px;
}
:deep(.n-avatar) {
  display: none !important;
}
:deep(.n-tag__content) {
  padding-left: 6px;
}

/* Normalize option icons to consistent size */
.option-icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.option-icon :deep(svg),
.option-icon :deep(img) {
  width: 100% !important;
  height: 100% !important;
  padding: 0 !important;
  object-fit: contain;
}
</style>
